// SchoolConfigPage.js
import React, { useState, useEffect } from 'react';
import { FormGroup, Input, Label, Button, Alert, Spinner, Card, CardBody, CardHeader, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import API from '../../services/API';

const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const SchoolConfigPage = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [mcReasons, setMCReasons] = useState([]);
  const [loadingMC, setLoadingMC] = useState(true);
  const [mcError, setMCError] = useState(null);
  const [selectedMC, setSelectedMC] = useState('');


  // NEW: Activation Date state
  const [activationDate, setActivationDate] = useState('');
  const [loadingActivation, setLoadingActivation] = useState(true);
  const [activationLocked, setActivationLocked] = useState(false);

  // Add this new state
  const [hasAfternoonSession, setHasAfternoonSession] = useState(false);
  // 🔹 New state for MC Letter toggle
  const [mcLetterEnabled, setMcLetterEnabled] = useState(true);
  const [loadingMCLetter, setLoadingMCLetter] = useState(true);
  const [savingMCLetter, setSavingMCLetter] = useState(false);

  const [timetableEnabled, setTimetableEnabled] = useState(false);
  const [loadingTimetable, setLoadingTimetable] = useState(true);
  const [savingTimetable, setSavingTimetable] = useState(false);

  useEffect(() => {
    fetchConfig();
    getMCReasons();
    fetchDefaultMC();
    fetchActivationDate();
    fetchMCLetterConfig();
    fetchTimetableConfig();
  }, []);
  const fetchTimetableConfig = async () => {
    setLoadingTimetable(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      };

      const res = await API.get("/timetable/enabled", { headers });
      if (res.data.response_code === 1000) {
        setTimetableEnabled(res.data.data.enabled);
      } else {
        toast.error(res.data.message || "Failed to load timetable config");
      }
    } catch (err) {
      console.log(err)
      toast.error("Error loading timetable config");
    } finally {
      setLoadingTimetable(false);
    }
  };
  const saveTimetableConfig = async (enabled) => {
    setSavingTimetable(true);

    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token")
      };

      const res = await API.post("/timetable/enabled", { enabled }, { headers });

      if (res.data.response_code === 1000) {
        setTimetableEnabled(enabled);
        toast.success("Timetable setting updated!");
      } else {
        toast.error(res.data.message || "Failed to save timetable setting");
      }
    } catch (err) {
      toast.error("Error updating timetable setting");
    } finally {
      setSavingTimetable(false);
    }
  };

  // 🔹 Fetch MC Letter configuration
  const fetchMCLetterConfig = async () => {
    setLoadingMCLetter(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.get('/portal/attendance/mcLetterConfig', { headers });

      if (res.data.response_code === 1000 && res.data.data) {
        setMcLetterEnabled(res.data.data.mc_letter_enabled);
      } else {
        toast.error(res.data.message || "Failed to load MC Letter configuration");
      }
    } catch (err) {
      toast.error("Error fetching MC Letter configuration");
    } finally {
      setLoadingMCLetter(false);
    }
  };

  // 🔹 Save MC Letter configuration
  const saveMCLetterConfig = async (enabled) => {
    setSavingMCLetter(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.post('/portal/attendance/mcLetterConfig', { mc_letter_enabled: enabled }, { headers });
      if (res.data.response_code === 1000) {
        setMcLetterEnabled(enabled);
        toast.success("MC Letter setting updated successfully");
      } else {
        toast.error(res.data.message || "Failed to update MC Letter setting");
      }
    } catch (err) {
      toast.error("Error updating MC Letter setting");
    } finally {
      setSavingMCLetter(false);
    }
  };

  // Fetch config
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };

      // Morning config
      const morningRes = await API.get('/portal/attendance/config', { headers });
      let morningData = {};
      if (morningRes.data.response_code === 1000) {
        morningData = morningRes.data.data || {};
      }

      let merged = { ...morningData };

      // Afternoon config
      let hasAfternoon = false;
      try {
        const afternoonRes = await API.get('/portal/attendance/afternoonConfig', { headers });
        if (afternoonRes.data.response_code === 1000) {
          const afternoonData = afternoonRes.data.data || {};
          const defaults = afternoonData["default"] || {};

          weekdays.forEach((_, idx) => {
            const perDay = afternoonData[String(idx)] || {};
            const mergedDay = { ...defaults, ...perDay }; // default fallback
            Object.entries(mergedDay).forEach(([k, v]) => {
              merged[`${k}_${idx}`] = v;
            });
          });

          hasAfternoon = true;
        } else if (afternoonRes.data.response_code === 4004) {
          // School has no afternoon session
          hasAfternoon = false;
        }
      } catch (err) {
        console.log("No afternoon session available or API error:", err);
        hasAfternoon = false;
      }

      setHasAfternoonSession(hasAfternoon);


      setConfig(merged);

      if (morningData.mc_category) {
        setSelectedMC(`${morningData.mc_category}|||${morningData.mc_sub_category || ''}`);
      }
    } catch (err) {
      setError("Failed to load config");
    } finally {
      setLoading(false);
    }
  };
  const fetchActivationDate = async () => {
    setLoadingActivation(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.get('/portal/schoolActivation', { headers });
      if (res.data.response_code === 1000 && res.data.data?.activation_date) {
        setActivationDate(res.data.data.activation_date.split('T')[0]);
        setActivationLocked(true); // Lock if already configured
      } else {
        setActivationLocked(false); //Allow new input
      }
    } catch {
      toast.error("Failed to load activation date");
    } finally {
      setLoadingActivation(false);
    }
  };

  // 🔹 NEW: Save school activation date
  const saveActivationDate = async () => {
    if (!activationDate) {
      toast.error("Please select an activation date.");
      return;
    }

    // Validation: cannot set past date or more than 1 year ahead
    const today = new Date();
    const selected = new Date(activationDate);
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(today.getFullYear() + 1);

    if (selected < today.setHours(0, 0, 0, 0)) {
      toast.error("Activation date cannot be in the past.");
      return;
    }
    if (selected > oneYearLater) {
      toast.error("Activation date cannot be more than 1 year ahead.");
      return;
    }

    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.post('/portal/schoolActivation', { activation_date: activationDate }, { headers });
      if (res.data.response_code === 1000) {
        toast.success("Activation date updated!");
      } else {
        toast.error(res.data.message || "Failed to update activation date");
      }
    } catch (err) {
      toast.error("Error saving activation date");
    } finally {
      setSaving(false);
    }
  };

  // Save all config
  const saveAll = async (isAfternoon = false) => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const payloads = [];

      if (!isAfternoon) {
        // Morning config
        ["early_cutoff", "late_after"].forEach(field => {
          const value = config[field];
          if (value && value.includes(":")) {
            const [hour, minute] = value.split(":").map(Number);
            payloads.push(API.post("/portal/attendance/config", {
              updated_field: field,
              value,         // keep raw for fallback
              hour,
              minute
            }, { headers }));
          }
        });
      } else {
        // Afternoon config (Mon–Fri)
        weekdays.forEach((_, idx) => {
          ["early_cutoff_afternoon", "late_after_afternoon"].forEach(field => {
            const key = `${field}_${idx}`;
            const value = config[key];
            if (value && value.includes(":")) {
              const [hour, minute] = value.split(":").map(Number);
              payloads.push(API.post("/portal/attendance/afternoonConfig", {
                updated_field: field,
                value,         // keep raw for fallback
                hour,
                minute,
                day_of_week: Number(idx)

              }, { headers }));
            }
          });
        });
      }

      const results = await Promise.all(payloads);

      // check failures
      results.forEach(res => {
        if (res.data?.response_code !== 1000) {
          throw new Error(res.data?.message || "Save failed");
        }
      });
      toast.success(isAfternoon ? "Afternoon config saved!" : "Morning config saved!");
      fetchConfig();
    } catch (err) {
      console.error("Save error", err);
      setError("Failed to save config");
    } finally {
      setSaving(false);
    }
  };


  // Fetch MC reasons
  const getMCReasons = async () => {
    setLoadingMC(true);
    try {
      const response = await fetch("https://api.schoolpay.tech/attendance/absenceReason");
      const resJSON = await response.json();
      if (resJSON.response_code === 1000) setMCReasons(resJSON.data);
      else setMCError(resJSON.message);
    } catch {
      setMCError("Failed to load MC reasons");
    } finally {
      setLoadingMC(false);
    }
  };

  const fetchDefaultMC = async () => {
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.get('/portal/attendance/mcConfig', { headers });
      if (res.data.response_code === 1000) {
        setConfig(cfg => ({
          ...cfg,
          mc_category: res.data.data.category || '',
          mc_sub_category: res.data.data.sub_category || '',
        }));
        if (res.data.data.category) {
          setSelectedMC(`${res.data.data.category}|||${res.data.data.sub_category || ''}`);
        }
      }
    } catch {
      setError("Failed to load MC default");
    }
  };

  const saveDefaultMC = async (e) => {
    e.preventDefault();
    if (!selectedMC) {
      toast.error("Please select a default MC Reason.");
      return;
    }
    const [mc_category, mc_sub_category] = selectedMC.split("|||");
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', token: localStorage.getItem('token') };
      const res = await API.post('/portal/attendance/mcConfig', { category: mc_category, sub_category: mc_sub_category }, { headers });
      console.log(res)
      if (res.data.response_code === 1000) {
        toast.success("MC Reason saved!");
        setConfig(cfg => ({ ...cfg, mc_category, mc_sub_category }));
      } else {
        setError(res.data.message);
      }
    } catch {
      setError("Failed to save MC reason");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container" style={{ marginTop: 30 }}>
      <Card style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
        <CardHeader style={{ fontSize: 20, fontWeight: 600 }}>Attendance Configuration</CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}
          {loading ? (
            <div style={{ textAlign: "center", marginTop: 40 }}><Spinner /></div>
          ) : (
            <>
              {/* Morning Config */}
              <h5>Morning Configuration</h5>
              <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                <FormGroup>
                  <Label>Early Cutoff</Label>
                  <Input type="time" value={config.early_cutoff || ""} onChange={e => setConfig({ ...config, early_cutoff: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label>Late After</Label>
                  <Input type="time" value={config.late_after || ""} onChange={e => setConfig({ ...config, late_after: e.target.value })} />
                </FormGroup>
              </div>
              <Button color="primary" disabled={saving} onClick={() => saveAll(false)}>Save Morning</Button>

              <hr />

            </>
          )}
        </CardBody>
      </Card>
      {hasAfternoonSession && (
        <Card>
          <CardHeader style={{ fontSize: 20, fontWeight: 600 }}>Afternoon Attendance Configuration</CardHeader>
          <CardBody>
            <Table bordered>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Early Cutoff</th>
                  <th>Late After</th>
                </tr>
              </thead>
              <tbody>
                {weekdays.map((day, idx) => (
                  <tr key={idx}>
                    <td>{day}</td>
                    <td>
                      <Input type="time"
                        value={config[`early_cutoff_afternoon_${idx}`] || ""}
                        onChange={e => setConfig({ ...config, [`early_cutoff_afternoon_${idx}`]: e.target.value })}
                      />
                    </td>
                    <td>
                      <Input type="time"
                        value={config[`late_after_afternoon_${idx}`] || ""}
                        onChange={e => setConfig({ ...config, [`late_after_afternoon_${idx}`]: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <hr />
            <Button color="primary" disabled={saving} onClick={() => saveAll(true)}>Save Afternoon</Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader style={{ fontSize: 20, fontWeight: 600 }}>MC Configurations</CardHeader>
        <CardBody>
          {/* MC Reason */}
          <h5>Default MC/Absent Reason</h5>
          {mcError && <Alert color="danger">{mcError}</Alert>}
          {loadingMC ? <Spinner size="sm" /> : (
            <form onSubmit={saveDefaultMC} style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <FormGroup style={{ minWidth: 280 }}>
                <Label>Select Default Reason</Label>
                <Input type="select" value={selectedMC} onChange={e => setSelectedMC(e.target.value)}>
                  <option value="">-- Please select --</option>
                  {mcReasons.map(r => (
                    <option key={r.reason_id} value={`${r.name}|||${r.sub_reason || ''}`}>
                      {r.name}{r.sub_reason ? ` — ${r.sub_reason}` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <Button color="success" disabled={saving} type="submit">Save Default</Button>
            </form>
          )}
          {config.mc_category && (
            <div style={{ marginTop: 12, fontWeight: 500 }}>
              Saved Default: <b>{config.mc_category}</b>{config.mc_sub_category ? ` — ${config.mc_sub_category}` : ""}
            </div>
          )}
          {loadingMCLetter ? (
            <Spinner size="sm" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Label style={{ fontWeight: 500, marginBottom: 4 }}>
                Require MC Letter Upload
              </Label>

              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  onClick={() => saveMCLetterConfig(!mcLetterEnabled)}
                  style={{
                    position: "relative",
                    width: 50,
                    height: 26,
                    borderRadius: 30,
                    backgroundColor: mcLetterEnabled ? "#28a745" : "#ccc",
                    cursor: savingMCLetter ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      left: mcLetterEnabled ? 26 : 3,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      transition: "left 0.3s",
                    }}
                  />
                </div>

                <span style={{ fontWeight: 500, color: mcLetterEnabled ? "#28a745" : "gray" }}>
                  {mcLetterEnabled ? "Enabled" : "Disabled"}
                </span>

                {savingMCLetter && <Spinner size="sm" />}
              </div>

              <p
                style={{
                  marginTop: 6,
                  fontStyle: "italic",
                  color: mcLetterEnabled ? "#28a745" : "gray",
                  transition: "color 0.3s",
                }}
              >
                When enabled, both students and teachers must upload an MC letter for medical absences.
              </p>
            </div>
          )}

        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {/* 🔹 NEW: School Activation Section */}
          <h5>School Activation</h5>
          {loadingActivation ? (
            <Spinner size="sm" />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 10 }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Activation Date</Label>
                <Input
                  type="date"
                  value={activationDate || ""}
                  onChange={(e) => setActivationDate(e.target.value)}
                  style={{ minWidth: 200 }}
                  disabled={activationLocked} // ✅ disable only if locked
                />
              </FormGroup>
              <Button
                color="success"
                disabled={saving || activationLocked} // ✅ disable only if locked
                onClick={saveActivationDate}
                style={{ height: "fit-content", marginTop: 22 }}
              >
                Save Activation Date
              </Button>

            </div>
          )}
          {activationDate && (
            <>
              <p style={{ marginTop: 8, color: "green", fontWeight: 500 }}>
                Activation date already configured: <b>{activationDate}</b>
              </p>
              <p style={{ marginTop: -5, color: "gray", fontStyle: "italic" }}>
                Contact your system administrator if you need to change the activation date.
              </p>
            </>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader style={{ fontSize: 20, fontWeight: 600 }}>
          Timetable Feature
        </CardHeader>
        <CardBody>
          {loadingTimetable ? (
            <Spinner size="sm" />
          ) : (
            <>
              <Label style={{ fontWeight: 500 }}>Enable Timetable</Label>

              <div
                onClick={() => saveTimetableConfig(!timetableEnabled)}
                style={{
                  marginTop: 10,
                  width: 55,
                  height: 28,
                  borderRadius: 30,
                  backgroundColor: timetableEnabled ? "#28a745" : "#ccc",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background-color 0.3s"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: timetableEnabled ? 28 : 3,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left 0.3s"
                  }}
                />
              </div>

              <p style={{ marginTop: 8 }}>
                Status:{" "}
                <b style={{ color: timetableEnabled ? "green" : "gray" }}>
                  {timetableEnabled ? "Enabled" : "Disabled"}
                </b>
              </p>
              {savingTimetable && <Spinner size="sm" />}
            </>
          )}
        </CardBody>
      </Card>

    </div>
  );
};

export default SchoolConfigPage;
